/**
 * Pure time-formatting helpers used across the UI. No side effects.
 * All functions accept an optional `now`/locale for deterministic tests.
 */

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

/** Short clock time, e.g. "14:05". Used on message bubbles / row previews. */
export function formatClock(ts: number, locale?: string): string {
  return new Date(ts).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Conversation-row timestamp: time if today, "Yesterday", weekday within the
 * last week, otherwise a short date.
 */
export function formatConversationTime(ts: number, now: number = Date.now(), locale?: string): string {
  const then = new Date(ts);
  const today = startOfDay(now);
  const thatDay = startOfDay(ts);
  const dayDiff = Math.round((today - thatDay) / MS_PER_DAY);

  if (dayDiff <= 0) return formatClock(ts, locale);
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff < 7) return then.toLocaleDateString(locale, { weekday: "long" });
  return then.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/** Full day label for the day-separator, e.g. "Today", "Yesterday", or a date. */
export function formatDaySeparator(ts: number, now: number = Date.now(), locale?: string): string {
  const dayDiff = Math.round((startOfDay(now) - startOfDay(ts)) / MS_PER_DAY);
  if (dayDiff <= 0) return "Today";
  if (dayDiff === 1) return "Yesterday";
  return new Date(ts).toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** True when two timestamps fall on different calendar days. */
export function isDifferentDay(a: number, b: number): boolean {
  return startOfDay(a) !== startOfDay(b);
}

/** Human "last active" string from a `lastActiveAgo` milliseconds value. */
export function formatLastActive(lastActiveAgoMs: number): string {
  if (lastActiveAgoMs < MS_PER_MINUTE) return "last seen just now";
  if (lastActiveAgoMs < MS_PER_HOUR) {
    const m = Math.floor(lastActiveAgoMs / MS_PER_MINUTE);
    return `last seen ${m} minute${m === 1 ? "" : "s"} ago`;
  }
  if (lastActiveAgoMs < MS_PER_DAY) {
    const h = Math.floor(lastActiveAgoMs / MS_PER_HOUR);
    return `last seen ${h} hour${h === 1 ? "" : "s"} ago`;
  }
  const d = Math.floor(lastActiveAgoMs / MS_PER_DAY);
  return `last seen ${d} day${d === 1 ? "" : "s"} ago`;
}

/** Format a millisecond duration as m:ss (for audio messages / recording). */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
